import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fbGoogle, fbSignIn, fbSignUp, fbReset, fbError, firebaseEnabled, syncRoanProfile, aafLoginUrl } from "@/lib/firebase";
import * as fb from "@/lib/firebase";
import { useMember } from "@/context/MemberContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const GoogleMark = () => (
  <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6C12.3 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 7.1-10 7.1-17.5z"/><path fill="#FBBC05" d="M10.4 28.8A14.5 14.5 0 0 1 9.5 24c0-1.7.3-3.3.9-4.8l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.8-6z"/><path fill="#34A853" d="M24 48c6.2 0 11.6-2 15.4-5.6l-7.5-5.8c-2 1.4-4.7 2.3-7.9 2.3-6.3 0-11.7-4.1-13.6-9.7l-7.8 6C6.5 42.6 14.6 48 24 48z"/></svg>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="font-type text-[11px] uppercase tracking-wider text-[#5A7355]">{label}</Label>
    {children}
  </div>
);

const FinishProfile = ({ pending, onDone }) => {
  const [name, setName] = useState(pending.name || "");
  const [postcode, setPostcode] = useState("");
  const [options, setOptions] = useState(null);
  const [electorate, setElectorate] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (options && !electorate) return toast.error("Choose your electorate");
    setBusy(true);
    try {
      const { data } = await api.post("/register", { name, email: pending.email, postcode, electorate: electorate || undefined, firebase_id_token: pending.idToken });
      if (data.needs_selection) return setOptions(data.electorates);
      onDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="sso-finish-profile" className="space-y-4">
      <div className="rounded-xl bg-[#E5EDE3] border border-[#8FA58A]/40 p-4 text-sm">
        <p className="font-semibold text-[#1B2A41]">Welcome, you're signed in as {pending.email}</p>
        <p className="text-[#5C6A79] mt-1">One more step for ROAN: your postcode places you in a federal electorate and issues your F.E.R.M member number.</p>
      </div>
      <Field label="Full name"><Input data-testid="sso-finish-name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} className="h-11" /></Field>
      <Field label="Postcode"><Input data-testid="sso-finish-postcode" required inputMode="numeric" pattern="[0-9]{4}" value={postcode} onChange={(e) => { setPostcode(e.target.value); setOptions(null); setElectorate(""); }} className="h-11" /></Field>
      {options && (
        <Field label="That postcode spans more than one electorate — pick yours">
          <div className="grid gap-2">
            {options.map((o) => (
              <button type="button" key={o.name} onClick={() => setElectorate(o.name)} data-testid={`sso-finish-option-${o.name}`} className={`text-left px-4 py-2.5 rounded-lg border text-sm ${electorate === o.name ? "border-[#E2705A] bg-[#E2705A]/10" : "border-[#D8D2C4] hover:border-[#8FA58A]"}`}>
                {o.name} <span className="text-xs text-[#5C6A79]">({o.state})</span>
              </button>
            ))}
          </div>
        </Field>
      )}
      <button type="submit" disabled={busy} data-testid="sso-finish-submit" className="w-full h-11 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 text-[#FDFBF7] font-type font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Join as a F.E.R.M
      </button>
    </form>
  );
};

export const AverageAfSignIn = () => {
  const { signIn, aaf, clearAaf, finishFirebaseSignIn } = useMember();
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(aaf?.pending || null);
  const [verify, setVerify] = useState(aaf?.verify || "");
  useEffect(() => {
    if (aaf?.pending) setPending(aaf.pending);
    if (aaf?.verify) setVerify(aaf.verify);
  }, [aaf]);
  if (!firebaseEnabled) return null;

  const finish = async (user) => {
    const result = await finishFirebaseSignIn(fb, user);
    if (result.verify) setVerify(result.verify);
    if (result.pending) setPending({ ...result.pending, name: result.pending.name || name });
  };

  const run = async (fn) => {
    setBusy(true);
    clearAaf();
    try {
      const cred = await fn();
      await finish(cred.user);
    } catch (e) {
      toast.error(e?.code ? fbError(e) : errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const goHub = () => {
    clearAaf();
    window.location.assign(aafLoginUrl());
  };

  const submit = (e) => {
    e.preventDefault();
    run(() => (mode === "signup" ? fbSignUp(email, password, name) : fbSignIn(email, password)));
  };

  const reset = async () => {
    if (!email) return toast.error("Enter your email first");
    try {
      await fbReset(email);
    } catch (e) {
      if (e?.code !== "auth/user-not-found") return toast.error(fbError(e));
    }
    toast.message("If an Average AF account uses that email, a reset link has been sent.", { description: "No account yet? Use “Email me a sign-in link”, or choose “Create account”.", duration: 9000 });
  };

  if (aaf?.busy) {
    return (
      <div data-testid="sso-aaf-busy" className="rounded-xl bg-[#E5EDE3] border border-[#8FA58A]/40 p-4 text-sm flex items-center gap-3 text-[#1B2A41]">
        <Loader2 className="w-4 h-4 animate-spin text-[#5A7355]" /> Signing you in with Average AF…
      </div>
    );
  }

  if (verify) {
    return (
      <div data-testid="sso-verify-notice" className="rounded-xl bg-[#E5EDE3] border border-[#8FA58A]/40 p-4 text-sm space-y-2">
        <p className="font-semibold text-[#1B2A41]">One quick check first</p>
        <p className="text-[#5C6A79]"><strong>{verify}</strong> is already a ROAN F.E.R.M. To connect it to your Average AF account we've emailed you a verification link — click it, then sign in here again. This protects your member number.</p>
        <button type="button" onClick={() => setVerify("")} className="text-xs text-[#5A7355] underline">Back</button>
      </div>
    );
  }

  if (pending) {
    return <FinishProfile pending={pending} onDone={(d) => { clearAaf(); signIn(d.token, d.member); syncRoanProfile(fb.fbAuth().currentUser, d.member).catch(() => {}); toast.success(`You're a F.E.R.M — ${d.member.member_number}`); }} />;
  }

  return (
    <div data-testid="sso-panel" className="space-y-4">
      <p className="text-sm text-[#5C6A79]">One account for ROAN, WOW Factor, My Vote Matters and every Average AF site.</p>
      {aaf?.error && (
        <div data-testid="sso-aaf-error" className="flex gap-2 rounded-xl bg-[#FFF4D6] border border-[#E8C870] px-4 py-3 text-xs text-[#6B4F00] leading-relaxed">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{aaf.error}</span>
        </div>
      )}
      <button type="button" onClick={goHub} disabled={busy} data-testid="sso-continue-aaf" className="w-full h-11 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 text-[#FDFBF7] font-type font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 transition-colors">
        <Sparkles className="w-4 h-4" /> Continue with Average AF
      </button>
      <div className="flex items-center gap-3 text-[11px] text-[#5C6A79] uppercase tracking-widest font-type"><span className="flex-1 h-px bg-[#D8D2C4]" /> or <span className="flex-1 h-px bg-[#D8D2C4]" /></div>
      <button type="button" onClick={() => run(fbGoogle)} disabled={busy} data-testid="sso-google" className="w-full h-11 rounded-full border border-[#D8D2C4] hover:border-[#1B2A41] bg-white text-[#1B2A41] font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors">
        <GoogleMark /> Continue with Google
      </button>
      <form onSubmit={submit} className="space-y-3 pt-1">
        <p className="text-[11px] text-[#5C6A79] font-type uppercase tracking-wider">{mode === "signup" ? "Create account" : "Log in with email"}</p>
        {mode === "signup" && <Field label="Full name"><Input data-testid="sso-name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} className="h-11" /></Field>}
        <Field label="Email"><Input data-testid="sso-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" /></Field>
        <Field label="Password"><Input data-testid="sso-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" /></Field>
        <button type="submit" disabled={busy} data-testid="sso-submit" className="w-full h-11 rounded-full bg-[#1B2A41] hover:bg-[#121E2B] disabled:opacity-60 text-[#FDFBF7] font-type font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {mode === "signup" ? "Create Average AF account" : "Sign in"}
        </button>
      </form>
      <div className="flex justify-between text-xs">
        <button type="button" onClick={() => setMode(mode === "signup" ? "signin" : "signup")} data-testid="sso-toggle-mode" className="text-[#5A7355] underline">{mode === "signup" ? "I already have an account" : "Create an Average AF account"}</button>
        {mode === "signin" && <button type="button" onClick={reset} data-testid="sso-reset" className="text-[#5C6A79] underline">Forgot password?</button>}
      </div>
    </div>
  );
};
